import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Loader2,
  Copy,
  Check,
  ExternalLink,
  Banknote,
  Users,
  Wallet,
  PiggyBank,
  ImagePlus,
  AtSign,
  Play,
  Camera,
  Music2,
  Globe,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  getReferralStatus,
  generateReferralCode,
  connectOnboard,
  connectCountries,
  connectRefresh,
  uploadCreatorLogo,
  updateSocialLinks,
} from '../../services/api';
import { AuthContext } from '../../context/AuthContext';
import UsernameEditor from '../studio/UsernameEditor';
import { Button } from 'components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from 'components/ui/card';
import { Badge } from 'components/ui/badge';
import { Alert, AlertDescription } from 'components/ui/alert';
import { Separator } from 'components/ui/separator';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';

// Customer-facing site the referral link points at (build-time var; the
// default is production — staging builds set REACT_APP_STOREFRONT_URL).
const STOREFRONT_URL = process.env.REACT_APP_STOREFRONT_URL || 'https://draftfc.co.uk';

const LOGO_SIZE = 256;

/**
 * Centre-crop an image file to a LOGO_SIZE × LOGO_SIZE square PNG via canvas.
 * Guarantees the server receives exactly 256x256 — the API validates only
 * type/size.
 */
function cropToSquarePng(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      try {
        const side = Math.min(img.naturalWidth, img.naturalHeight);
        const sx = (img.naturalWidth - side) / 2;
        const sy = (img.naturalHeight - side) / 2;
        const canvas = document.createElement('canvas');
        canvas.width = LOGO_SIZE;
        canvas.height = LOGO_SIZE;
        const ctx = canvas.getContext('2d');
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, sx, sy, side, side, 0, 0, LOGO_SIZE, LOGO_SIZE);
        canvas.toBlob((blob) => {
          URL.revokeObjectURL(url);
          if (!blob) return reject(new Error('Could not process image'));
          resolve(new File([blob], 'logo.png', { type: 'image/png' }));
        }, 'image/png');
      } catch (e) {
        URL.revokeObjectURL(url);
        reject(e);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not read image'));
    };
    img.src = url;
  });
}

function StatCard({ icon: Icon, label, value }) {
  return (
    <Card>
      <CardContent className="flex items-center gap-3 p-4">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-muted">
          <Icon className="h-4 w-4 text-secondary" aria-hidden="true" />
        </span>
        <span className="min-w-0">
          <span className="block text-token-xl font-bold leading-tight">{value}</span>
          <span className="block truncate text-token-sm text-muted-foreground">{label}</span>
        </span>
      </CardContent>
    </Card>
  );
}

function StepBadge({ done, number }) {
  return (
    <span
      className={
        done
          ? 'flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-token-sm font-semibold text-primary-foreground'
          : 'flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-borderGrey text-token-sm font-semibold text-textSecondary'
      }
    >
      {done ? <Check className="h-3.5 w-3.5" aria-hidden="true" /> : number}
    </span>
  );
}

const gbp = (n) =>
  new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(n ?? 0);

const SOCIAL_FIELDS = [
  { key: 'twitter', label: 'Twitter / X', icon: AtSign, placeholder: 'https://x.com/yourhandle' },
  { key: 'youtube', label: 'YouTube', icon: Play, placeholder: 'https://youtube.com/@yourchannel' },
  { key: 'instagram', label: 'Instagram', icon: Camera, placeholder: 'https://instagram.com/yourhandle' },
  { key: 'tiktok', label: 'TikTok', icon: Music2, placeholder: 'https://tiktok.com/@yourhandle' },
  { key: 'website', label: 'Website / content', icon: Globe, placeholder: 'https://yoursite.com' },
];

const EMPTY_LINKS = { twitter: '', youtube: '', instagram: '', tiktok: '', website: '' };

export default function ReferralCentre() {
  const { user } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null); // {status, totals, lastPayout, logoUrl}
  const [error, setError] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [copied, setCopied] = useState(false);
  const [links, setLinks] = useState(EMPTY_LINKS);
  const [savingLinks, setSavingLinks] = useState(false);
  const [countries, setCountries] = useState([]);
  const [country, setCountry] = useState('');
  const fileInputRef = useRef(null);
  const refreshedRef = useRef(false);

  const load = useCallback(async () => {
    try {
      const res = await getReferralStatus();
      setData(res);
      setLinks({ ...EMPTY_LINKS, ...Object.fromEntries(
        Object.entries(res.socialLinks || {}).map(([k, v]) => [k, v || '']),
      ) });
      // Prefill from the existing account; new creators must pick explicitly.
      setCountry((prev) => prev || res.status?.connect?.country || '');
      setError(null);
      return res;
    } catch (err) {
      setError('Failed to load referral centre — please refresh.');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const res = await load();
      if (cancelled || !res) return;

      // After Stripe onboarding bounce-back (?connect=return / refresh), or
      // when an account exists but payouts aren't live yet, pull fresh state
      // from Stripe instead of waiting for the storefront webhook.
      const params = new URLSearchParams(location.search);
      const fromStripe = params.get('connect');
      const stale =
        res.status?.connect?.hasAccount && !res.status?.connect?.payoutsEnabled;
      if (!refreshedRef.current && (fromStripe || stale)) {
        refreshedRef.current = true;
        try {
          await connectRefresh();
          if (!cancelled) await load();
        } catch {
          // no account yet / transient — the page still renders
        }
        if (fromStripe) {
          navigate('/referrals', { replace: true });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [load, location.search, navigate]);

  // Country list is only needed while the Stripe account is still changeable.
  useEffect(() => {
    if (!data || data.status?.connect?.detailsSubmitted || countries.length) return;
    connectCountries()
      .then((res) => setCountries(res?.countries || []))
      .catch(() => {
        // Selector shows a retry hint; the rest of the page still works.
      });
  }, [data, countries.length]);

  const handleGenerateCode = async () => {
    setGenerating(true);
    try {
      await generateReferralCode();
      toast.success('Referral link ready');
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || 'Failed to generate referral link');
    } finally {
      setGenerating(false);
    }
  };

  const handleConnect = async () => {
    const detailsSubmitted = !!data?.status?.connect?.detailsSubmitted;
    if (!detailsSubmitted && !country) {
      toast.error('Select your payout country first');
      return;
    }
    setConnecting(true);
    try {
      const res = await connectOnboard(detailsSubmitted ? null : country);
      if (res?.url) {
        window.location.assign(res.url);
        return;
      }
      toast.error('Stripe did not return a link — try again');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to open Stripe');
    } finally {
      setConnecting(false);
    }
  };

  const handleLogoFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploadingLogo(true);
    try {
      const squared = await cropToSquarePng(file);
      const res = await uploadCreatorLogo(squared);
      // Bust the browser cache — the S3 key is fixed per creator.
      setData((d) => (d ? { ...d, logoUrl: `${res.url}?v=${Date.now()}` } : d));
      toast.success('Logo updated');
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || 'Logo upload failed');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSaveLinks = async () => {
    setSavingLinks(true);
    try {
      const res = await updateSocialLinks(links);
      setLinks({ ...EMPTY_LINKS, ...Object.fromEntries(
        Object.entries(res.socialLinks || {}).map(([k, v]) => [k, v || '']),
      ) });
      toast.success('Links saved');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save links');
    } finally {
      setSavingLinks(false);
    }
  };

  const copyLink = async (link) => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Could not copy — long-press or select the link instead');
    }
  };

  if (loading) {
    return (
      <div className="flex h-[50vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" aria-hidden="true" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <Alert variant="destructive">
        <AlertDescription>{error || 'Something went wrong.'}</AlertDescription>
      </Alert>
    );
  }

  const { status, totals, lastPayout, logoUrl } = data;
  const hasUsername = !!status?.niceUsername;
  const payoutsEnabled = !!status?.connect?.payoutsEnabled;
  const hasAccount = !!status?.connect?.hasAccount;
  const detailsSubmitted = !!status?.connect?.detailsSubmitted;
  const lockedCountry = status?.connect?.country || null;
  const countryName = (code) =>
    countries.find((c) => c.code === code)?.name ||
    (() => {
      try {
        return new Intl.DisplayNames(['en'], { type: 'region' }).of(code) || code;
      } catch {
        return code;
      }
    })();
  const code = status?.code || null;
  const referralLink = code ? `${STOREFRONT_URL}/?ref=${code}` : null;

  return (
    <div className="mx-auto w-full max-w-4xl space-y-6">
      <div>
        <h1 className="text-token-2xl font-bold">Referral Centre</h1>
        <p className="text-token-md text-muted-foreground">
          Earn 25% of every payment made by members you refer — for as long as they stay
          subscribed. Payouts every Monday via Stripe.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard icon={Users} label="Lifetime referrals" value={totals?.lifetimeReferrals ?? 0} />
        <StatCard icon={Check} label="Currently subscribed" value={totals?.currentlySubscribed ?? 0} />
        <StatCard icon={Wallet} label="Unpaid balance" value={gbp(totals?.unpaidBalanceGbp)} />
        <StatCard icon={PiggyBank} label="Lifetime earned" value={gbp(totals?.lifetimeEarnedGbp)} />
      </div>

      {lastPayout && (
        <p className="text-token-sm text-muted-foreground">
          Last payout: {gbp(lastPayout.amountGbp)} ({lastPayout.status}) — week ending{' '}
          {lastPayout.weekEnding}. Paid out to date: {gbp(totals?.lifetimePaidOutGbp)}.
        </p>
      )}

      {/* Step 1 — display name */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-token-lg">
            <StepBadge done={hasUsername} number={1} /> Choose your display name
          </CardTitle>
          <CardDescription>
            Shown to members you refer and used to build your referral link.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <UsernameEditor
            user={user}
            initialUsername={status?.niceUsername || ''}
            onUsernameUpdate={() => load()}
          />
        </CardContent>
      </Card>

      {/* Step 2 — Stripe Connect */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-token-lg">
            <StepBadge done={payoutsEnabled} number={2} /> Set up payouts
            {payoutsEnabled && <Badge variant="success">Payouts active</Badge>}
            {hasAccount && !payoutsEnabled && <Badge variant="warning">Incomplete</Badge>}
          </CardTitle>
          <CardDescription>
            Connect a bank account through Stripe to receive your weekly commission payouts.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {detailsSubmitted ? (
            lockedCountry && (
              <p className="text-token-sm text-muted-foreground">
                Payout country: <span className="font-medium text-foreground">{countryName(lockedCountry)}</span>{' '}
                — locked to your verified Stripe account. Contact us if you need to change it.
              </p>
            )
          ) : (
            <div className="max-w-xs space-y-1.5">
              <Label htmlFor="connect-country">Your country</Label>
              <Select value={country} onValueChange={setCountry} disabled={!hasUsername}>
                <SelectTrigger id="connect-country">
                  <SelectValue placeholder="Select your country" />
                </SelectTrigger>
                <SelectContent>
                  {countries.map((c) => (
                    <SelectItem key={c.code} value={c.code}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-token-sm text-muted-foreground">
                Where you live and hold a bank account. This is permanent once your Stripe
                account is verified, so make sure it&apos;s right.
                {country && country !== 'GB' && (
                  <> Payouts are calculated in GBP and converted to your local currency by Stripe.</>
                )}
                {(country === 'US' || country === 'CA') && (
                  <> Stripe will ask a few extra business questions during setup — answer as an individual.</>
                )}
              </p>
              {!countries.length && (
                <p className="text-token-sm text-destructive">
                  Couldn&apos;t load the country list — refresh the page to try again.
                </p>
              )}
            </div>
          )}
          <Button
            onClick={handleConnect}
            disabled={connecting || !hasUsername || (!detailsSubmitted && !country)}
          >
            {connecting ? (
              <Loader2 className="animate-spin" />
            ) : (
              <ExternalLink aria-hidden="true" />
            )}
            {payoutsEnabled
              ? 'Open Stripe dashboard'
              : hasAccount
                ? 'Finish Stripe setup'
                : 'Connect with Stripe'}
          </Button>
          {!hasUsername && (
            <p className="mt-2 text-token-sm text-muted-foreground">
              Set your display name first.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Step 3 — referral link */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-token-lg">
            <StepBadge done={!!code} number={3} /> Share your link
          </CardTitle>
          <CardDescription>
            New members who join through your link get their first month free on a Monthly
            Pass — and you earn 25% of what they pay, for life.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {referralLink ? (
            <div className="flex flex-wrap items-center gap-2">
              <code className="rounded-token-medium bg-muted px-3 py-2 text-token-md">
                {referralLink}
              </code>
              <Button variant="outline" size="sm" onClick={() => copyLink(referralLink)}>
                {copied ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}
                {copied ? 'Copied' : 'Copy'}
              </Button>
            </div>
          ) : (
            <Button onClick={handleGenerateCode} disabled={generating || !hasUsername}>
              {generating && <Loader2 className="animate-spin" />}
              Generate my link
            </Button>
          )}
          {code && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleGenerateCode}
              disabled={generating}
            >
              {generating && <Loader2 className="animate-spin" />}
              Refresh link from my display name
            </Button>
          )}
        </CardContent>
      </Card>

      <Separator />

      {/* Branding / logo */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-token-lg">
            <Banknote className="hidden" aria-hidden="true" />
            Your logo
          </CardTitle>
          <CardDescription>
            Square logo (256×256) shown alongside your content on DraftFC. Uploading a new
            image replaces the current one.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex items-center gap-4">
          <div className="flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-token-large border border-borderGrey bg-muted">
            {logoUrl ? (
              <img src={logoUrl} alt="Your logo" className="h-full w-full object-cover" />
            ) : (
              <ImagePlus className="h-8 w-8 text-muted-foreground" aria-hidden="true" />
            )}
          </div>
          <div className="space-y-2">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
              onChange={handleLogoFile}
            />
            <Button
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingLogo}
            >
              {uploadingLogo ? <Loader2 className="animate-spin" /> : <ImagePlus aria-hidden="true" />}
              {logoUrl ? 'Replace logo' : 'Upload logo'}
            </Button>
            <p className="text-token-sm text-muted-foreground">
              PNG, JPG or WebP, up to 2MB. We crop to a centred square automatically.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Social / content links */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-token-lg">Your links</CardTitle>
          <CardDescription>
            Where members can find your content. Shown alongside your profile on DraftFC.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            {SOCIAL_FIELDS.map(({ key, label, icon: Icon, placeholder }) => (
              <div key={key} className="space-y-1.5">
                <Label htmlFor={`link-${key}`} className="flex items-center gap-1.5">
                  <Icon className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
                  {label}
                </Label>
                <Input
                  id={`link-${key}`}
                  type="url"
                  inputMode="url"
                  placeholder={placeholder}
                  value={links[key]}
                  onChange={(e) => setLinks((l) => ({ ...l, [key]: e.target.value }))}
                />
              </div>
            ))}
          </div>
          <Button onClick={handleSaveLinks} disabled={savingLinks}>
            {savingLinks && <Loader2 className="animate-spin" />}
            Save links
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
